import clsx from 'clsx';
import { isEmpty, uniqBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Banner, FormSelect, Loader, Skeleton, useCartContext, useLeisurePurchaseContext } from 'components';
import { formatLocalizedCurrency } from 'helpers';
import { useFetch, useUser } from 'hooks';
import { setLeisurePurchase } from 'store';

const LeisureSummaryStep = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { currentUser } = useUser();
  const { next } = useLeisurePurchaseContext();
  const { cart } = useCartContext();
  const { setValue, control, watch } = useFormContext();

  const {
    call: shippingMethodCall,
    data: shippingMethod,
    setData: setShippingMethod,
    loading: shippingMethodLoading
  } = useFetch({ withSetState: false });
  const { call: saveShippingMethodCall, loading: saveMethodLoading } = useFetch({ withSetState: false });

  const storedShippingMethodChoice = useSelector((state) => state.leisurePurchase.shippingMethod);
  const selectedShippingMethod = watch('summary.shippingMethod');

  const allProductsHaveShippingMethod = useMemo(
    () => shippingMethod?.lines?.every((line) => line.shippingMethod !== null),
    [shippingMethod]
  );

  const showBanner = useMemo(() => {
    // Banner info is shown if at least one shipping method is registered_mail or tracked_mail
    const mailShippingMethods = ['registered_mail', 'tracked_mail'];
    return ['lines', 'mandatory', 'choices'].some((key) =>
      shippingMethod?.[key]?.some((method) => mailShippingMethods.includes(method.shippingMethod || method.value))
    );
  }, [shippingMethod]);

  useEffect(() => {
    getShippingMethods();
  }, []);

  useEffect(() => {
    if (shippingMethod?.lines) setShippingMethodPrices();
  }, [shippingMethod]);

  const getShippingMethods = async () => {
    if (!currentUser?.serialNumber) return;

    const { data } =
      (await shippingMethodCall({
        service: 'checkout',
        method: 'get',
        path: `/${currentUser.serialNumber}/cart/ticketing/shipping-method`
      })) || {};

    if (!isEmpty(data?.body)) {
      const choicesOptions = data?.body?.choices?.map((choice) => ({
        label: choice.shippingMethodLabel,
        value: choice.shippingMethod,
        price: choice.price
      }));
      setValue('summary.shippingMethod', storedShippingMethodChoice || choicesOptions[0]);
      setShippingMethod({ ...data.body, choices: choicesOptions });
    }
  };

  const saveShippingMethod = async () => {
    if (!currentUser?.serialNumber) return null;

    const { data } =
      (await saveShippingMethodCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser.serialNumber}/cart/ticketing/shipping-method/${selectedShippingMethod.value}`
      })) || {};
    if (data?.status === 200) {
      dispatch(setLeisurePurchase({ key: 'shippingMethod', value: selectedShippingMethod }));
      dispatch(setLeisurePurchase({ key: 'mandatory', value: shippingMethod?.mandatory }));
      return data.status;
    }
  };

  const renderShippingMethods = (cartLine) => {
    const { choices, lines } = shippingMethod || {};
    const productLine = lines?.find((product) => product.lineId === cartLine.id);
    const choicesOptions = choices?.map((choice) => ({
      ...choice,
      lineId: productLine?.lineId
    }));
    if (!productLine) return '';

    const handleShippingMethodChange = (shippingOption) => {
      setValue('summary.shippingMethod', shippingOption);
      setShippingMethodPrices(shippingOption);
    };

    if (isEmpty(productLine?.shippingMethodLabel)) {
      if (choicesOptions?.length > 1) {
        return (
          <form>
            <Controller
              name="summary.shippingMethod"
              control={control}
              render={() => (
                <FormSelect
                  options={choicesOptions}
                  onChange={handleShippingMethodChange}
                  name="summary.shippingMethod"
                />
              )}
            />
          </form>
        );
      } else {
        return choices?.[0]?.label ?? '';
      }
    } else {
      return productLine.shippingMethodLabel;
    }
  };

  const setShippingMethodPrices = (method = selectedShippingMethod) => {
    const { mandatory, choices, lines } = shippingMethod || {};
    let shippingPrices = [];

    lines.forEach((line) => {
      const shippingMethodToUse = line.shippingMethod === null ? method.value : line.shippingMethod;

      let priceInfo;

      /**
       * Shipping methods are not defined for all products
       * If a product has no shipping method, check either in choices or mandatory arrays for the price
       * Else, prices are defined in mandatory array
       */
      if (!allProductsHaveShippingMethod) {
        if (shippingMethodToUse === method.value) {
          priceInfo = choices.find((item) => item.value === shippingMethodToUse);
        } else {
          priceInfo = mandatory.find((item) => item.shippingMethod === shippingMethodToUse);
        }
      } else {
        priceInfo = mandatory.find((item) => item.shippingMethod === line.shippingMethod);
      }

      shippingPrices.push({
        value: priceInfo.price,
        key: shippingMethodToUse
      });
    });

    shippingPrices = uniqBy(shippingPrices, 'key');
    const shippingTotalPrice = shippingPrices.reduce((acc, item) => acc + item.value, 0);
    dispatch(setLeisurePurchase({ key: 'shippingPrice', value: shippingTotalPrice }));
  };

  const onNextClick = async () => {
    /**
     * Saving shipping isn't necessary if all products have a shipping method
     * Default status to 200 to allow navigation if all products have a shipping method
     */
    let status = 200;

    if (!allProductsHaveShippingMethod) {
      const shippingMethodCallStatus = await saveShippingMethod();
      status = shippingMethodCallStatus;
    }

    setShippingMethodPrices();

    if (status === 200) {
      // Navigate to next step
      next();
    }
  };

  return (
    <>
      {!shippingMethodLoading && showBanner && (
        <Banner icon={{ name: 'mailbox', style: 'fad' }} type="warning" className="my-6 w-fit">
          <p>{t('purchase:summary.bannerInfo')}</p>
        </Banner>
      )}

      <h2 className="hidden underlined-blue lg:mb-4 lg:block lg:h2">{t('purchase:summary.title')}</h2>

      {shippingMethodLoading && <Skeleton classNames="w-full h-96" />}

      {!shippingMethodLoading && (
        <>
          <div className="bg-gray-200 p-2 border-b border-gray-200 rounded-t-2xl mt-6">
            <div className="grid grid-cols-3 lg:grid-cols-12 uppercase">
              <div className="text-center lg:col-span-6">{t('purchase:summary.table.label')}</div>
              <div className="text-center lg:col-span-1">{t('purchase:summary.table.price')}</div>
              <div className="text-center lg:col-span-1">{t('purchase:summary.table.quantity')}</div>
              <div className="text-center hidden lg:block lg:col-span-4">
                {t('purchase:summary.table.shippingMethod')}
              </div>
            </div>
          </div>

          {cart?.lines?.map((line, index) => (
            <div
              key={`ticket-line-${index}`}
              className={clsx(
                // eslint-disable-next-line max-len
                'grid grid-cols-3 lg:grid-cols-12 gap-2 p-2 border-b border-x border-gray-200 bg-white items-center',
                index + 1 === cart?.lines?.length && 'rounded-b-2xl'
              )}
            >
              <div className="truncate-line four-lines lg:col-span-6">{line.label}</div>
              <div className="text-center self-center lg:col-span-1">{formatLocalizedCurrency(line.price / 100)}</div>
              <div className="text-center self-center lg:col-span-1">{line.quantity}</div>
              <div className="col-span-2 uppercase lg:text-center lg:col-span-4">{renderShippingMethods(line)}</div>
            </div>
          ))}
        </>
      )}

      <button
        type="button"
        onClick={onNextClick}
        className="btn purchase-btn my-4"
        disabled={shippingMethodLoading || saveMethodLoading}
      >
        {saveMethodLoading && <Loader loaderClass="w-4 h-4 shrink-0 text-white mr-2" />}
        {t('buttons.continue')}
      </button>
    </>
  );
};

export default LeisureSummaryStep;
